import Fuse from 'fuse.js';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDebounce, useI18n } from '~Hooks';

import { LANGUAGES } from '~Types';
import { CompleteBongQuery_venue_company_categories_items as BongQueryItem } from '~Scenes/POS/Queries/__queries__';
import styled from '@emotion/styled';
import { POSButton, useBongLayout } from '~Providers/BongLayout';
import { useEntryFinder } from '~Components/EntryFinder';
import Icon from '~ComponentsImproved/Icon';
import CloseIcon from '~Assets/icons/close.svg';
import SearchIcon from '~Assets/icons/search.svg';
import { Box, Input, SearchInputWrapper, TransparentButton } from '~Styles';
import { BASE_ROW_HEIGHT, ItemWrapper } from '.';
import ItemButton from './ItemButton';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { useTableUniverse } from '~Components/TableUniverse';
import { useCart } from '~Components/CartProvider';
import { BlackButton } from '~Scenes/Handy/styles';

// Fuse.js configuration for searching items by title in all supported languages
const titleKeys = LANGUAGES.map((lang) => `titleLang.${lang}`);

const fuse = new Fuse<BongQueryItem>([], {
  keys: titleKeys,
  includeScore: true,
  // Threshold needs to be set to 0 to get the results we are after.
  threshold: 0,
  // Ignore location helps us search through the whole item name for a match.
  ignoreLocation: true,
});

const CloseButton = styled(TransparentButton)`
  border-radius: 100%;
  height: 32px;
  width: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  flex-shrink: 0;
  border: 1px solid var(--colorBlueGray600);
  margin: 8px auto 8px auto;
  background-color: var(--colorBlueGray1200);
`;

/** Search button that floats at the top center */
const SearchButton = styled(BlackButton)`
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 6;
  width: 40px;
  height: 16px;
  padding: 0;
  border-radius: 0 0 10px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateX(-50%) ${({ touching }) => (touching ? 'scale(0.95)' : 'scale(1)')};
`;

const Wrapper = styled(Box)`
  margin: 0;
  width: calc(100% - 32px);
  height: calc(100%);
  max-height: calc(100% - 40px);
  overflow-y: scroll;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};

  /** This is to make it work for android units (like S1F2) because when the keyboard turns up the height of the screen shrinks by ~50% */
  @media (min-height: 400px) {
    height: calc(100% - 96px);
    margin: 0 0 25% 0;
  }
`;

interface Props {
  handleItemPress: (
    item: BongQueryItem,
    manualPrice?: number,
    volume?: number,
    message?: string,
  ) => void;
  setSubPage: (subPage: any) => void;
}

/** Handheld only - For searching & adding products to cart/cheque (works in QSR & TSR) */
const SearchItem: FC<Props> = ({ handleItemPress, setSubPage }) => {
  const { i18n } = useI18n();
  const layout = useBongLayout();
  const findEntryFromItem = useEntryFinder();
  const chequeContext = useChequeContext();
  const tableUniverse = useTableUniverse();
  const cart = useCart();
  const page = usePage();
  const isQSR = page?.current === 'POS';

  const [input, setInputValue] = useState<string>('');
  const debouncedSearch = useDebounce(input, 300);

  // Convert itemIdMap to array for staged bundle
  const allItems = useMemo(() => {
    return Object.values(layout.itemIdMap);
  }, [layout.itemIdMap]);

  // Filter all items from itemIdMap based on the debounced search value
  const filteredButtons = useMemo(() => {
    if (!debouncedSearch) {
      return [];
    }

    // Search using Fuse.js and filter to only include items with valid entries
    const results = fuse
      .search(debouncedSearch)
      .map((result) => result.item)
      .filter((item) => findEntryFromItem(item.id));

    // Map results to POSButton format for ItemButton component
    return results.map(
      (item): POSButton => {
        const category = layout.itemToCategoryMap[item.id];

        return {
          id: item.id,
          type: 'ITEM',
          nodeId: item.id,
          color: category?.color ?? 'var(--colorBlueGray50)',
          label: i18n(item.titleLang),
          x: null,
          y: null,
        };
      },
    );
  }, [debouncedSearch, layout.itemToCategoryMap, i18n, findEntryFromItem]);

  const itemsOnCheque = useMemo(
    () =>
      [...tableUniverse.items].filter(
        (item) => item.chequeId === chequeContext.selectedCheque?.id,
      ),
    [tableUniverse.items],
  );

  const onPageClicked = useCallback((pageClicked: { id: string; name: string }) => {
    layout.browseToSubpage(pageClicked);
  }, []);

  // If we are in QSR mode, we only want to count the items in the cart otherwise it will be doubled
  const itemsForCount = isQSR ? cart.packagedItems : [...itemsOnCheque, ...cart.packagedItems];

  // Update Fuse collection when allItems changes
  useEffect(() => {
    fuse.setCollection(allItems);
  }, [allItems]);

  const productSearchModal = useModal();

  // Open the search modal directly
  const searchButton = useButton({
    onClick: () => productSearchModal.toggle(),
  });

  const closeSearchButton = useButton({
    onClick: () => {
      productSearchModal.toggle();
      setInputValue('');
    },
  });

  return (
    <>
      <SearchButton {...searchButton}>
        <Icon src={SearchIcon} color="var(--colorBlueGray200)" width="16px" height="16px" />
      </SearchButton>

      <Modal {...productSearchModal} zIndex={8}>
        <CloseButton
          display="flex"
          alignItems="center"
          justifyContent="center"
          {...closeSearchButton}>
          <Icon src={CloseIcon} color="var(--colorBlueGray200)" width="16px" height="16px" />
        </CloseButton>

        <Wrapper
          display="flex"
          flexDirection="column"
          gap="xs"
          justifyContent="flex-start"
          alignItems="center"
          padding="medium">
          <SearchInputWrapper fluid value={input} onClear={() => setInputValue('')}>
            <Input
              id="search-articles"
              name="search-articles"
              placeholder={`${i18n('POS.Search')}...`}
              value={input}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
              dark
              padding="medium"
              isSearch
              autoFocus
              fluid
            />
          </SearchInputWrapper>

          <ItemWrapper hasActiveLayout={false} rowHeight={BASE_ROW_HEIGHT}>
            {filteredButtons.map((button) => (
              <ItemButton
                key={button.nodeId}
                button={button}
                onPress={handleItemPress}
                onPageClicked={onPageClicked}
                count={itemsForCount.filter((item) => item.itemId === button.nodeId).length ?? 0}
                setSubPage={setSubPage}
              />
            ))}
          </ItemWrapper>
        </Wrapper>
      </Modal>
    </>
  );
};

export default SearchItem;
